import { useTranslation } from 'react-i18next';
// frontend/src/components/admin/CategoryForm.jsx - Category Form for Admin
import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Switch,
    Typography,
    Alert
} from '@mui/material';

const CategoryForm = ({ open, onClose, onSave, category, categories = [] }) => {
  const { t } = useTranslation();
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        slug: '',
        parent_id: '',
        is_active: true,
        sort_order: 0
    });

    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (category) {
            setFormData({
                name: category.name || '',
                description: category.description || '',
                slug: category.slug || '',
                parent_id: category.parent_id || '',
                is_active: category.is_active !== undefined ? category.is_active : true,
                sort_order: category.sort_order || 0
            });
        } else {
            setFormData({
                name: '',
                description: '',
                slug: '',
                parent_id: '',
                is_active: true,
                sort_order: 0
            });
        }
        setErrors({});
    }, [category, open]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));

        // Auto-generate slug from name
        if (field === 'name' && !category) {
            const slug = value
                .toLowerCase()
                .trim()
                .replace(/[^a-z0-9\s-]/g, '')
                .replace(/\s+/g, '-')
                .replace(/-+/g, '-');
            setFormData(prev => ({ ...prev, slug }));
        }

        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: null }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = t('hardcoded.components_admin_categoryform.nombre_es_requerido');
        }

        if (!formData.slug.trim()) {
            newErrors.slug = t('hardcoded.components_admin_categoryform.slug_es_requerido');
        } else if (!/^[a-z0-9-]+$/.test(formData.slug)) {
            newErrors.slug = t('hardcoded.components_admin_categoryform.slug_solo_puede_contener_letras');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = () => {
        if (!validateForm()) return;

        const saveData = {
            ...formData,
            parent_id: formData.parent_id || null,
            sort_order: parseInt(formData.sort_order) || 0
        };

        onSave(saveData);
    };

    // Filter out the current category from parent options to prevent self-reference
    const parentOptions = categories.filter(cat =>
        !category || cat.id !== category.id
    );

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {category ? t('hardcoded.components_admin_categoryform.editar_categoria') : t('hardcoded.components_admin_categoryform.crear_categoria')}
            </DialogTitle>

            <DialogContent dividers>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label={t('hardcoded.components_admin_categoryform.nombre_de_la_categoria')}
                            value={formData.name}
                            onChange={(e) => handleChange('name', e.target.value)}
                            error={!!errors.name}
                            helperText={errors.name}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Slug *"
                            value={formData.slug}
                            onChange={(e) => handleChange('slug', e.target.value)}
                            error={!!errors.slug}
                            helperText={errors.slug || t('hardcoded.components_admin_categoryform.url_amigable_se_genera_automaticamente')}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <InputLabel>{t('hardcoded.components_admin_categoryform.categoria_padre')}</InputLabel>
                            <Select
                                value={formData.parent_id}
                                label={t('hardcoded.components_admin_categoryform.categoria_padre')}
                                onChange={(e) => handleChange('parent_id', e.target.value)}
                            >
                                <MenuItem value="">
                                    <em>{t('hardcoded.components_admin_categoryform.categoria_principal')}</em>
                                </MenuItem>
                                {parentOptions.map(cat => (
                                    <MenuItem key={cat.id} value={cat.id}>
                                        {cat.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label={t('audit.table.description')}
                            value={formData.description}
                            onChange={(e) => handleChange('description', e.target.value)}
                            multiline
                            rows={3}
                            helperText={t('hardcoded.components_admin_categoryform.descripcion_opcional_de_la_categoria')}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label={t('hardcoded.components_admin_categoryform.orden_de_clasificacion')}
                            type="number"
                            value={formData.sort_order}
                            onChange={(e) => handleChange('sort_order', e.target.value)}
                            helperText={t('hardcoded.components_admin_categoryform.numero_mas_bajo_aparece_primero')}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.is_active}
                                    onChange={(e) => handleChange('is_active', e.target.checked)}
                                />
                            }
                            label={t('hardcoded.components_admin_categoryform.categoria_activa')}
                        />
                    </Grid>

                    {formData.parent_id && (
                        <Grid item xs={12}>
                            <Alert severity="info">
                                {t('hardcoded.components_admin_categoryform.esta_sera_una_subcategoria_de')}{
                                    categories.find(cat => cat.id === formData.parent_id)?.name
                                }
                            </Alert>
                        </Grid>
                    )}
                </Grid>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>
                    {t('common.cancel')}</Button>
                <Button onClick={handleSave} variant="contained">
                    {category ? t('audit.actions.UPDATE') : t('audit.actions.CREATE')} {t('hardcoded.components_admin_categoryform.categoria')}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default CategoryForm;